import React, { useMemo, useState, useRef, useEffect } from "react";
import PhoneInput, {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
} from "react-phone-number-input";
import en from "react-phone-number-input/locale/en.json";
import { Check, ChevronDown, Search } from "lucide-react";

/**
 * PhoneField — international phone input with country flag, calling code,
 * and searchable country list. Uses react-phone-number-input under the hood
 * but re-skins the country picker to match the HuddleUp design system.
 *
 * Props:
 *   value           string  E.164 phone (e.g. "+971501234567")
 *   country         string  ISO 3166 alpha-2 (e.g. "AE")
 *   onValueChange   ({ value, country, countryCode, countryName }) => void
 *   defaultCountry  default ISO code, defaults to "AE"
 *   testId          data-testid prefix
 *   required        bool
 *   invalid         bool
 */
export default function PhoneField({
  value,
  country,
  onValueChange,
  defaultCountry = "AE",
  testId = "phone",
  required = false,
  invalid = false,
}) {
  const [countryOpen, setCountryOpen] = useState(false);
  const [search, setSearch] = useState("");
  const activeCountry = country || defaultCountry;
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!countryOpen) return undefined;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setCountryOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [countryOpen]);

  const countries = useMemo(() => {
    const list = getCountries().map((c) => ({
      code: c,
      name: en[c] || c,
      dial: `+${getCountryCallingCode(c)}`,
    }));
    // Priority order at top
    const priority = ["SA", "AE", "GB", "US", "CA", "IN", "PK", "QA", "BH", "KW", "SG"];
    const top = priority
      .map((code) => list.find((x) => x.code === code))
      .filter(Boolean);
    const rest = list
      .filter((x) => !priority.includes(x.code))
      .sort((a, b) => a.name.localeCompare(b.name));
    return [...top, ...rest];
  }, []);

  const filtered = useMemo(() => {
    if (!search) return countries;
    const q = search.toLowerCase();
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dial.includes(q),
    );
  }, [countries, search]);

  const setCountry = (c) => {
    setCountryOpen(false);
    setSearch("");
    onValueChange({
      value: "",
      country: c.code,
      countryCode: c.dial,
      countryName: c.name,
    });
  };

  const onPhoneChange = (nextValue) => {
    const dial = `+${getCountryCallingCode(activeCountry)}`;
    onValueChange({
      value: nextValue || "",
      country: activeCountry,
      countryCode: dial,
      countryName: en[activeCountry] || activeCountry,
    });
  };

  const activeCountryData = countries.find((c) => c.code === activeCountry);

  return (
    <div className="relative" data-testid={`${testId}-wrapper`} ref={wrapperRef}>
      <div
        className={`flex items-stretch border-b transition-colors duration-500 ${
          invalid ? "border-red-500" : "border-charcoal/25 focus-within:border-gold"
        }`}
      >
        <button
          type="button"
          data-testid={`${testId}-country-trigger`}
          onClick={() => setCountryOpen((v) => !v)}
          className="flex items-center gap-2 pr-4 py-3 border-r border-charcoal/15 hover:text-gold transition-colors duration-500"
          aria-label="Select country"
        >
          <span className="text-sm font-medium text-charcoal">
            {activeCountryData?.code || activeCountry}
          </span>
          <span className="text-xs text-charcoal/60">
            {activeCountryData?.dial}
          </span>
          <ChevronDown size={14} className="text-charcoal/50" />
        </button>

        <PhoneInput
          key={activeCountry}
          data-testid={testId}
          defaultCountry={activeCountry}
          international={false}
          addInternationalOption={false}
          countries={[activeCountry]}
          limitMaxLength={true}
          value={value}
          onChange={onPhoneChange}
          required={required}
          placeholder="50 123 4567"
          className="flex-1 pl-4"
          numberInputProps={{
            "data-testid": `${testId}-input`,
            className:
              "w-full bg-transparent pb-3 pt-1 font-display text-lg text-charcoal outline-none placeholder-charcoal/25",
          }}
        />
      </div>

      {countryOpen && (
        <div
          data-testid={`${testId}-country-list`}
          className="absolute z-30 mt-2 left-0 right-0 max-w-md bg-white border border-charcoal/15 shadow-2xl max-h-[360px] flex flex-col"
        >
          <div className="flex items-center gap-2 px-4 py-3 border-b border-charcoal/10">
            <Search size={14} className="text-charcoal/50" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search country or code"
              data-testid={`${testId}-country-search`}
              className="flex-1 bg-transparent outline-none text-sm text-charcoal placeholder-charcoal/40"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && (
              <div className="px-4 py-6 text-sm text-charcoal/50">No match.</div>
            )}
            {filtered.map((c) => (
              <button
                key={c.code}
                type="button"
                data-testid={`${testId}-country-option-${c.code}`}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setCountry(c); }}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-cream transition-colors ${
                  c.code === activeCountry ? "bg-cream" : ""
                }`}
              >
                <span className="flex items-center gap-3">
                  <span className="text-xs font-medium text-charcoal/50 w-8">
                    {c.code}
                  </span>
                  <span className="text-charcoal">{c.name}</span>
                </span>
                <span className="flex items-center gap-3">
                  <span className="text-xs text-charcoal/60">{c.dial}</span>
                  {c.code === activeCountry && <Check size={14} className="text-gold" />}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export { isValidPhoneNumber };
